/**
 * Family presence store — Supabase-sourced 3-state statuses for the ACTIVE
 * family. All status indicators across the app (invite panel, family member
 * list, header/user menu) read from here exclusively (AC-13/AC-14).
 *
 * `statuses` holds only members with a live presence key (`online`/`away`);
 * anyone absent is `offline` — use {@link selectMemberStatus}.
 *
 * @module features/p2p-chat/stores/familyPresenceStore
 */

import { create } from 'zustand';
import { registerFamilyScopedReset } from '@/shared/lib/storeReset';
import type {
  PresenceStatus,
  TrackablePresenceStatus,
} from '../lib/familyPresence';

interface FamilyPresenceState {
  /** profileId → merged status (absent ⇒ offline). */
  readonly statuses: Readonly<Record<string, TrackablePresenceStatus>>;
  /** Our own reported status (drives the header/user-menu self indicator). */
  readonly myStatus: PresenceStatus;
  setStatuses: (
    statuses: Readonly<Record<string, TrackablePresenceStatus>>
  ) => void;
  setMyStatus: (status: PresenceStatus) => void;
  reset: () => void;
}

export const useFamilyPresenceStore = create<FamilyPresenceState>((set) => ({
  statuses: {},
  myStatus: 'offline',
  setStatuses: (statuses) => set({ statuses }),
  setMyStatus: (myStatus) => set({ myStatus }),
  reset: () => set({ statuses: {}, myStatus: 'offline' }),
}));

/** Resolve a member's status (absent presence key ⇒ offline). */
export function selectMemberStatus(
  statuses: Readonly<Record<string, TrackablePresenceStatus>>,
  profileId: string
): PresenceStatus {
  return statuses[profileId] ?? 'offline';
}

// Presence is family-scoped: switching family (or logging out) clears it so
// the next scope never renders the previous family's statuses.
registerFamilyScopedReset(() => {
  useFamilyPresenceStore.getState().reset();
});
